import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { GoogleAdWordsLinkedService, googleAdWordsAuthTypes, apiTypes } from 'app/model/linkedservice/google-adwords.linkedservice';
import { GoogleAdWordsCopySource } from 'app/model/pipeline/copysource/google-adwords.copysource';
import { format } from 'app/shared/utils/string.util';
import { IRadioOption } from 'app/interfaces/IRadioOption';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { googleAdwordsReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';
import { driverVersionRecommendedOptions, driverVersions } from '../consts/tabular-consts';

const apiTypeOptions: Array<IRadioOption<apiTypes>> = [
    {
        label: stringResources.GoogleAdWords_DisplayText,
        disabled: true,
        value: apiTypes.GoogleAds
    },
    {
        label: stringResources.GoogleAdWords_Legacy_DisplayText,
        disabled: true,
        value: apiTypes.GoogleAdWords
    }
];

const parameterizedControl = (commonControl: ControlMetadata): ControlMetadata => {
    // only process the tree nodes, should not embedded the child control inside the commonControl
    if (commonControl.type === 'textbox') {
        return { type: 'parameterizable', hide: commonControl.hide, child: commonControl };
    } else if (commonControl.type === 'dynamic-akv-switcher') {
        const { bind, child } = commonControl;
        const paramlizedControl: ControlMetadata = {
            enableAKVParameterization: true,
            hideAKVSwitcherInConnStrProps: true,
            ...commonControl,
        };
        paramlizedControl.child = {
            type: 'parameterizable',
            attribute: bind,
            disableAddParameter: true,
            child: { ...child }
        };
        return paramlizedControl;
    }
    return commonControl;
};

const clientCustomerIDControl: ControlMetadata = {
    type: 'textbox',
    bind: 'clientCustomerID',
    label: stringResources.ModelName_ClientCustomerId,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};

const developerTokenControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'developerToken',
    label: stringResources.ModelName_DeveloperToken,
    child: {
        type: 'password',
        label: stringResources.ModelName_DeveloperToken,
        isLabelVertical: true,
        required: true
    },
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        stringResources.GoogleAdWords_DisplayText,
        stringResources.ModelName_DeveloperToken)
};

const emailControl: ControlMetadata = {
    type: 'textbox',
    bind: 'email',
    label: stringResources.EmailLabel,
    isLabelVertical: true,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ]
};

const serviceAuthCommonControlsNew: ControlMetadata[] = [
    emailControl,
    {
        type: 'dynamic-akv-switcher',
        bind: 'privateKey',
        label: stringResources.ModelName_PrivateKey,
        child: {
            type: 'password',
            label: stringResources.ModelName_PrivateKey,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.GoogleAdWords_DisplayText,
            stringResources.ModelName_ClientSecret)
    }
];

const serviceAuthCommonControlsOld: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'email',
        label: stringResources.EmailLabel,
        isLabelVertical: true,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ]
    },
    {
        type: 'textbox',
        bind: 'keyFilePath',
        label: stringResources.ModelName_KeyFilePath,
        isLabelVertical: true,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ]
    },
    {
        type: 'textbox',
        bind: 'trustedCertPath',
        label: stringResources.ModelName_TrustedCertPath,
        isLabelVertical: true
    },
    {
        type: 'radio',
        bind: 'useSystemTrustStore',
        label: stringResources.ModelName_UseSystemTrustStore,
        defaultValue: false,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.True,
                value: true
            },
            {
                label: stringResources.False,
                value: false
            }
        ]
    }
];

const userAuthCommonControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'clientId',
        label: stringResources.ModelName_ClientId,
        isLabelVertical: true,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ]
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'clientSecret',
        label: stringResources.ModelName_ClientSecret,
        child: {
            type: 'password',
            label: stringResources.ModelName_ClientSecret,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.GoogleAdWords_DisplayText,
            stringResources.ModelName_ClientSecret)
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'refreshToken',
        label: stringResources.ModelName_RefreshToken,
        child: {
            type: 'password',
            label: stringResources.ModelName_RefreshToken,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.GoogleAdWords_DisplayText,
            stringResources.ModelName_RefreshToken)
    }
];

const loginCustomerIDControl: ControlMetadata = {
    type: 'textbox',
    bind: 'loginCustomerID',
    label: stringResources.ModelName_LoginCustomerId,
    isLabelVertical: true
};

const authSwitchControls = (isNewControl: boolean): ControlMetadata => {
    return {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        options: [
            {
                label: stringResources.ModelName_ServiceAuthentication,
                value: googleAdWordsAuthTypes.ServiceAuthentication
            },
            {
                label: stringResources.ModelName_UserAuthentication_NoPreview,
                value: googleAdWordsAuthTypes.UserAuthentication
            }
        ],
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        defaultValue: googleAdWordsAuthTypes.UserAuthentication,
        cases: [
            {
                caseValue: googleAdWordsAuthTypes.ServiceAuthentication,
                children: isNewControl ? serviceAuthCommonControlsNew.map(parameterizedControl) : serviceAuthCommonControlsOld.map(parameterizedControl)
            },
            {
                caseValue: googleAdWordsAuthTypes.UserAuthentication,
                children: userAuthCommonControls.map(parameterizedControl)
            }
        ]
    };
};

const commonControls = (hasLogin: boolean, isNewControl: boolean): ControlMetadata[] => {
    const controls = [parameterizedControl(clientCustomerIDControl)];
    if (hasLogin) {
        controls.push(parameterizedControl(loginCustomerIDControl));
    }
    return [
        ...controls,
        parameterizedControl(developerTokenControl),
        authSwitchControls(isNewControl)
    ];
};

const keyValListControl: ControlMetadata = {
    type: 'key-val-list',
    bind: 'additionalFields',
    hide: '{ isOldResource }',
    label: stringResources.AdditionalConnectionStringFields,
    reservedKeys: googleAdwordsReservedKeys
};

export const googleAdWordsMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(GoogleAdWordsLinkedService).name,
        displayName: stringResources.GoogleAdWords_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-google_adwords'
        },
        isFirstClass: true,
        supportFirstClassParameterExperience: true,
        isDeprecated: '{ !isOldResource && !googleAdsApiVersion }',
        showWarningMessage: '{ !isOldResource && !googleAdsApiVersion }', // show warning message iff using Legacy driver
        warningMessage: stringResources.GoogleAdwords_unsupport_legacy,
        warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-google-adwords?tabs=data-factory#upgrade-the-google-ads-driver-version',
        supportDatasetV2: true,
        category: ['Database'],
        copySinkTypes: [],
        copySourceTypes: [Metamodel.modelFor(GoogleAdWordsCopySource).name],
        controls: [
            {
                type: 'radio-switch',
                bind: 'apiType',
                hide: '{ !isOldResource }',
                label: stringResources.GoogleAdWords_ApiType_DisplayText,
                isLabelVertical: true,
                options: apiTypeOptions,
                inlineHintMessage: stringResources.GoogleAdWords_VersionDeprecate,
                hintMode: 'warning',
                defaultValue: apiTypes.GoogleAds,
                cases: [
                    {
                        caseValue: apiTypes.GoogleAds,
                        children: []
                    },
                    {
                        caseValue: apiTypes.GoogleAdWords,
                        children: commonControls(false, false)
                    }
                ]
            },
            {
                type: 'radio-switch',
                bind: 'driverVersion',
                isLabelVertical: true,
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                label: stringResources.DriverVersion,
                hide: `{ isOldResource }`,
                defaultValue: driverVersions.Recommended,
                options: driverVersionRecommendedOptions(true),
                hideOptions: `{ driverVersion ===  '${driverVersions.Recommended}'}`, // hide options if already using recommended driver
                cases: [
                    {
                        caseValue: driverVersions.Recommended,
                        children: [
                            {
                                type: 'textbox',
                                bind: 'googleAdsApiVersion',
                                label: stringResources.GoogleAdWords_AdsApiVersion_DisplayText,
                                defaultValue: 'v14',
                                description: stringResources.GoogleAdWords_AdsApiVersion_Desc,
                                escapeDescription: false,
                                descriptionLink: 'https://developers.google.com/google-ads/api/docs/release-notes',
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' },
                                    {
                                        type: 'pattern',
                                        pattern: '^v(\\d)+$',
                                        errorMessage: stringResources.GoogleAdWords_ApiVersion_FormatErr
                                    }
                                ],
                                placeholder: 'e.g. v14',
                                isLabelVertical: true
                            },
                            ...commonControls(true, true)
                        ]
                    },
                    {
                        caseValue: driverVersions.Legacy,
                        children: commonControls(true, false)
                    }
                ]
            },
            // for current w/o ff
            keyValListControl
        ]
    };

    return metadata;
};
